import React, { useEffect, useState } from "react";
import { listAll } from "../../services/firestore";
import {
  Package, Tags, User, ShoppingCart, FileText, Cloud, AlertTriangle,
  BarChart3, Users, Percent, Ticket, Bell, Plus, TrendingUp,
} from "lucide-react";
import { Link, useOutletContext } from "react-router-dom";
import { getR2Config } from "../../services/r2";

function Stat({ icon: Icon, label, value, link, accent = false, hint }) {
  const inner = (
    <div className={`group border ${accent ? "border-[var(--primary)]" : "border-[var(--border)]"} p-6 hover:border-[var(--primary)] hover:bg-[var(--surface)] transition-all`}>
      <div className="flex items-center justify-between">
        <Icon size={18} className="text-[var(--primary)]" />
        {hint && (
          <span className="metadata text-[10px] text-[var(--text-muted)] group-hover:text-[var(--accent)] transition-colors">
            {hint}
          </span>
        )}
      </div>
      <div className="font-display text-4xl mt-3">{value}</div>
      <div className="metadata mt-1 text-[var(--text-2)]">{label}</div>
    </div>
  );
  return link ? <Link to={link} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{inner}</Link> : inner;
}

function QuickAction({ to, icon: Icon, label, desc, accent }) {
  return (
    <Link
      to={to}
      className={`group flex items-start gap-4 p-5 border transition-all ${
        accent
          ? "border-[var(--accent)] bg-[var(--accent)]/5 hover:bg-[var(--accent)]/10"
          : "border-[var(--border)] hover:border-[var(--primary)] hover:bg-[var(--surface)]"
      }`}
      data-testid={`quick-${to.split("/").pop()}`}
    >
      <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${accent ? "bg-[var(--accent)] text-white" : "bg-[var(--surface)]"}`}>
        <Icon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-display text-base group-hover:text-[var(--primary)] transition-colors">{label}</div>
        <div className="metadata mt-1 text-[var(--text-muted)] text-[10px]">{desc}</div>
      </div>
    </Link>
  );
}

export default function AdminDashboard() {
  const ctx = useOutletContext();
  const [counts, setCounts] = useState({
    products: 0, categories: 0, artists: 0, orders: 0, customers: 0, revenue: 0,
    discountedProducts: 0, activeCoupons: 0,
  });
  const [r2Ok, setR2Ok] = useState(null);

  useEffect(() => {
    Promise.all([
      listAll("products"), listAll("categories"), listAll("artists"),
      listAll("orders"), listAll("users"), listAll("coupons").catch(() => []),
    ]).then(([p, c, a, o, u, cp]) => {
      // Ödənilməmiş sifarişləri (awaiting_payment / payment_failed) hər
      // statistikadan çıxarırıq — yalnız real ödənilmiş sifarişlər sayılır.
      const paidOrders = o.filter((x) => x.status !== "awaiting_payment" && x.status !== "payment_failed");
      setCounts({
        products: p.length, categories: c.length, artists: a.length, orders: paidOrders.length,
        customers: u.length,
        revenue: paidOrders.reduce((s, x) => s + (Number(x.total_azn) || 0), 0),
        discountedProducts: p.filter((x) => Number(x.discount_pct || 0) > 0).length,
        activeCoupons: cp.filter((x) => x.active !== false).length,
      });
    }).catch(() => {});
    getR2Config().then((cfg) => setR2Ok(!!(cfg?.buckets?.length))).catch(() => setR2Ok(false));
  }, [ctx?.seeded]);

  return (
    <div className="p-10" data-testid="admin-dashboard">
      <div className="metadata text-[var(--accent)]">İDARƏ MƏRKƏZİ</div>
      <h1 className="font-display text-5xl mt-2">Salam, Admin.</h1>
      <p className="text-[var(--text-2)] text-sm mt-3 max-w-xl">
        Mağazanızın canlı statistikası və tez-tez istifadə olunan əməliyyatlar.
      </p>

      {r2Ok === false && (
        <Link to="/admin/buckets" className="mt-8 flex items-center gap-3 border border-[var(--accent)] p-4 text-sm hover:bg-[var(--accent)]/10" data-testid="r2-warning">
          <AlertTriangle size={18} className="text-[var(--accent)]" />
          <div>
            <div className="font-display text-lg">R2 bucket konfiqurasiya edilməyib</div>
            <div className="metadata mt-1 text-[var(--text-2)]">Şəkil yükləmək üçün Cloudflare R2 bucket əlavə edin →</div>
          </div>
        </Link>
      )}

      {/* Stats */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
        <Stat icon={ShoppingCart} label="ÜMUMİ SİFARİŞ" value={counts.orders} link="/admin/orders" accent />
        <Stat icon={TrendingUp} label="ÜMUMİ GƏLİR (AZN)" value={`₼${counts.revenue.toFixed(0)}`} link="/admin/analytics" accent />
        <Stat icon={Users} label="MÜŞTƏRİ" value={counts.customers} link="/admin/customers" accent />
        <Stat icon={Package} label="MƏHSUL" value={counts.products} link="/admin/products" hint={counts.discountedProducts > 0 ? `${counts.discountedProducts} endirimdə` : null} />
        <Stat icon={Tags} label="KATEQORİYA" value={counts.categories} link="/admin/categories" />
        <Stat icon={User} label="SƏNƏTÇİ" value={counts.artists} link="/admin/artists" />
      </div>

      {/* Quick Actions */}
      <div className="mt-12">
        <div className="metadata text-[var(--text-muted)] mb-4">TEZ ƏMƏLİYYATLAR</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <QuickAction
            to="/admin/discounts"
            icon={Percent}
            label="Kütləvi Endirim"
            desc="Bütün məhsullara birdən endirim tətbiq et"
            accent
          />
          <QuickAction
            to="/admin/products"
            icon={Plus}
            label="Yeni Məhsul"
            desc="Kataloqa yeni vinil əlavə et"
          />
          <QuickAction
            to="/admin/coupons"
            icon={Ticket}
            label="Kupon Yarat"
            desc="Promo kod ilə müştəri cəlb et"
          />
          <QuickAction
            to="/admin/notifications"
            icon={Bell}
            label="Bildiriş Göndər"
            desc="Müştərilərə yenilik bildir"
          />
          <QuickAction
            to="/admin/content"
            icon={FileText}
            label="Sayt Mətnləri"
            desc="Hero, footer və banner mətnləri"
          />
          <QuickAction
            to="/admin/analytics"
            icon={BarChart3}
            label="Analitika"
            desc="Detallı statistika və hesabatlar"
          />
        </div>
      </div>

      {/* Bottom links */}
      <div className="mt-10 grid md:grid-cols-2 gap-4">
        <Link to="/admin/content" className="border border-[var(--border)] p-6 hover:border-[var(--primary)] transition-colors" data-testid="link-content">
          <FileText size={18} className="text-[var(--primary)]" />
          <div className="font-display text-2xl mt-3">Sayt Mətnləri</div>
          <div className="metadata mt-1 text-[var(--text-2)]">Hero, footer və digər sayt mətnləri</div>
        </Link>
        <Link to="/admin/buckets" className="border border-[var(--border)] p-6 hover:border-[var(--primary)] transition-colors" data-testid="link-buckets">
          <Cloud size={18} className="text-[var(--primary)]" />
          <div className="font-display text-2xl mt-3">Najafli Said</div>
          <div className="metadata mt-1 text-[var(--text-2)]">Şəkil saxlama bucket-ləri</div>
        </Link>
      </div>
    </div>
  );
}
